import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AppleLogo, GoogleLogo, DesktopTower, DownloadSimple, CheckCircle, ShareNetwork, CaretRight, ArrowSquareOut, Info } from "@phosphor-icons/react";
import { detectDevice, detectBrowser, isStandalone, captureInstallPrompt } from "@/lib/installUtils";
import PawLinkLogo from "@/components/PawLinkLogo";

/**
 * PawLink /install page — the canonical landing target for the install QR
 * and the "Install PawLink" button.
 *
 * Behaviour (identical on Web, PWA landing, future Android + iOS shells):
 *   • iOS Safari       → shows the visual "Share → Add to Home Screen"
 *                        walkthrough (Apple does not expose a programmatic
 *                        prompt on iOS).
 *   • Android Chrome / Desktop Chromium browsers → automatically fires the
 *                        native `beforeinstallprompt` on mount. One tap,
 *                        real home-screen install.
 *   • Firefox / other  → shows browser-specific "How to install" steps so
 *                        the page NEVER dead-ends.
 *   • Already installed → auto-redirects the user into the app.
 */
const PLAY_STORE_URL = process.env.REACT_APP_PLAY_STORE_URL || ""; // populated when we ship the APK
const APP_STORE_URL = process.env.REACT_APP_APP_STORE_URL || "";
const APK_URL = "/downloads/pawlink-latest.apk";

async function isApkAvailable() {
    // Only claim APK ready when the response is a real Android package.
    try {
        const h = await fetch(APK_URL, { method: "HEAD", cache: "no-cache" });
        if (!h.ok) return false;
        const ct = (h.headers.get("content-type") || "").toLowerCase();
        return ct.includes("android.package-archive") || ct.includes("application/octet-stream");
    } catch { return false; }
}

const BROWSER_STEPS = {
    chrome:   ["Open the menu (⋮) in the top-right corner.", "Tap \"Install app\" / \"Add to Home screen\".", "Confirm — PawLink will open like a real app."],
    edge:     ["Open the menu (…) in the top-right corner.", "Tap \"Apps\" → \"Install this site as an app\".", "Confirm to add PawLink to your device."],
    brave:    ["Open the Brave menu (☰).", "Tap \"Install PawLink\".", "Confirm — PawLink will appear on your home screen."],
    opera:    ["Open the Opera menu.", "Tap \"Install PawLink\".", "Confirm to add PawLink to your desktop."],
    samsung:  ["Tap the menu (☰) at the bottom of the browser.", "Tap \"Add page to\" → \"Home screen\".", "Confirm to add PawLink to your home screen."],
    firefox:  ["Open the Firefox menu (⋮).", "Tap \"Install\" or \"Add to Home Screen\".", "Confirm — PawLink will appear on your home screen."],
    safari:   ["Tap the Share button ↑ at the bottom of Safari.", "Scroll and tap \"Add to Home Screen\".", "Tap \"Add\" in the top-right to confirm."],
    other:    ["Open your browser's main menu.", "Look for \"Install app\", \"Add to Home Screen\", or \"Install this site as an app\".", "Confirm to add PawLink."],
};

export default function Install() {
    const device = useMemo(() => detectDevice(), []);
    const browser = useMemo(() => detectBrowser(), []);
    const [status, setStatus] = useState("detecting"); // detecting | ready | prompting | installed | need-steps | native-ready
    const [nativeUrl, setNativeUrl] = useState(""); // Play Store / App Store URL if applicable
    const [promptReady, setPromptReady] = useState(false);
    const promptRef = useRef(null);

    // 0. If we're already inside the installed PWA, redirect to Home.
    useEffect(() => {
        if (isStandalone()) window.location.replace("/home");
    }, []);

    // 1. Capture beforeinstallprompt so we can trigger it below.
    useEffect(() => {
        const off = captureInstallPrompt((e) => {
            promptRef.current = e;
            setPromptReady(true);
            setStatus((s) => (s === "detecting" || s === "need-steps" ? "ready" : s));
        });
        return off;
    }, []);

    const firePrompt = async () => {
        if (!promptRef.current) return;
        setStatus("prompting");
        try {
            await promptRef.current.prompt();
            const choice = await promptRef.current.userChoice;
            if (choice?.outcome === "accepted") {
                setStatus("installed");
            } else {
                setStatus("need-steps");
            }
            promptRef.current = null;
            setPromptReady(false);
        } catch {
            setStatus("need-steps");
        }
    };

    // 2. Decide what to do on mount, based on device + native store URLs.
    useEffect(() => {
        (async () => {
            // If native store URLs are configured (published apps), route straight there.
            if (device === "android" && PLAY_STORE_URL) {
                setNativeUrl(PLAY_STORE_URL);
                setStatus("native-ready");
                window.location.href = PLAY_STORE_URL;
                return;
            }
            if (device === "ios" && APP_STORE_URL) {
                setNativeUrl(APP_STORE_URL);
                setStatus("native-ready");
                window.location.href = APP_STORE_URL;
                return;
            }
            // Android with a signed APK download (advanced early-access path).
            if (device === "android") {
                const apk = await isApkAvailable();
                if (apk) {
                    setStatus("prompting");
                    const a = document.createElement("a");
                    a.href = APK_URL; a.download = "PawLink.apk"; a.rel = "noopener";
                    document.body.appendChild(a); a.click(); a.remove();
                    setTimeout(() => setStatus("ready"), 3000);
                    return;
                }
            }
            // iOS Safari — no programmatic prompt exists on iOS, always show steps.
            if (device === "ios") { setStatus("need-steps"); return; }

            // Give the browser up to 1.2s to fire beforeinstallprompt (may
            // arrive slightly after our mount). If it does not fire, show
            // the browser-specific manual install walkthrough.
            setTimeout(() => {
                setStatus((s) => (s === "detecting" ? "need-steps" : s));
            }, 1200);
        })();
    }, [device]);

    // ---------- Render ----------
    const steps = BROWSER_STEPS[browser] || BROWSER_STEPS.other;
    const deviceLabel = device === "ios" ? "iPhone / iPad" : device === "android" ? "Android" : "Desktop";
    const browserLabel = ({
        chrome: "Chrome", edge: "Microsoft Edge", brave: "Brave", opera: "Opera",
        samsung: "Samsung Internet", firefox: "Firefox", safari: "Safari",
    })[browser] || "your browser";

    return (
        <div dir="ltr" className="min-h-screen w-full flex flex-col items-center justify-start bg-paw-ink text-paw-bone px-6 pt-14 pb-24">
            <div className="w-full max-w-md">

                {/* Brand */}
                <div className="flex items-center justify-center mb-8" data-testid="install-page-brand">
                    <PawLinkLogo variant="full" size={54} className="text-paw-bone" />
                </div>

                {/* State card */}
                <div className="rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-md p-6 sm:p-8 shadow-2xl" data-testid={`install-state-${status}`}>

                    {(status === "detecting" || status === "prompting" || status === "native-ready") && (
                        <StateInstalling label={
                            status === "native-ready" ? "Opening the app store…" :
                            status === "prompting" ? "Waiting for your confirmation…" :
                            "Getting PawLink ready for your device…"
                        } />
                    )}

                    {status === "installed" && (
                        <StateInstalled />
                    )}

                    {status === "ready" && promptReady && (
                        <StateReadyToInstall
                            deviceLabel={deviceLabel}
                            browserLabel={browserLabel}
                            onInstall={firePrompt}
                        />
                    )}

                    {status === "ready" && !promptReady && (
                        <StateInstalling label="Your browser is preparing the install prompt…" />
                    )}

                    {status === "need-steps" && (
                        <StateSteps
                            device={device}
                            deviceLabel={deviceLabel}
                            browserLabel={browserLabel}
                            steps={steps}
                            promptReady={promptReady}
                            onInstall={firePrompt}
                        />
                    )}

                </div>

                <p className="text-center text-[12px] text-paw-bone/45 mt-6 leading-relaxed">
                    Detected: <span className="text-paw-bone/70">{deviceLabel}</span> · <span className="text-paw-bone/70">{browserLabel}</span>
                </p>

                <div className="text-center mt-2 text-[13px]">
                    <Link to="/" data-testid="install-back-home" className="text-paw-terra hover:underline">Back to PawLink →</Link>
                </div>
            </div>
        </div>
    );
}

/* -------------------- state fragments -------------------- */

function StateInstalling({ label }) {
    return (
        <div className="flex flex-col items-center text-center py-4">
            <div className="relative w-16 h-16 rounded-full bg-paw-terra/20 flex items-center justify-center mb-5">
                <div className="absolute inset-0 rounded-full border-2 border-paw-terra/60 animate-ping" />
                <DownloadSimple size={30} weight="bold" className="text-paw-terra" />
            </div>
            <h1 className="text-2xl font-semibold text-paw-bone mb-2">Installing PawLink</h1>
            <p className="text-paw-bone/60 text-sm max-w-sm">{label}</p>
        </div>
    );
}

function StateInstalled() {
    return (
        <div className="flex flex-col items-center text-center py-4" data-testid="install-success">
            <div className="w-16 h-16 rounded-full bg-emerald-500/15 flex items-center justify-center mb-5">
                <CheckCircle size={34} weight="fill" className="text-emerald-400" />
            </div>
            <h1 className="text-2xl font-semibold text-paw-bone mb-2">PawLink is installed!</h1>
            <p className="text-paw-bone/60 text-sm max-w-sm mb-6">Open PawLink from your home screen — you&apos;re ready to protect your pet.</p>
            <a href="/home" data-testid="install-open" className="inline-flex items-center gap-2 rounded-full bg-paw-terra text-white font-semibold px-6 py-3 hover:brightness-110 transition">
                Open PawLink <CaretRight size={16} weight="bold" />
            </a>
        </div>
    );
}

function StateReadyToInstall({ deviceLabel, browserLabel, onInstall }) {
    return (
        <div className="text-center py-2" data-testid="install-ready">
            <div className="w-16 h-16 mx-auto rounded-full bg-paw-terra/15 flex items-center justify-center mb-5">
                <DownloadSimple size={30} weight="bold" className="text-paw-terra" />
            </div>
            <h1 className="text-2xl font-semibold text-paw-bone mb-2">Ready to install PawLink</h1>
            <p className="text-paw-bone/60 text-sm max-w-sm mx-auto mb-6">
                One tap and PawLink lives on your home screen. No app store required — this is the real PawLink app running as a fast, offline-ready installation.
            </p>
            <button
                type="button"
                onClick={onInstall}
                data-testid="install-cta"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-paw-terra text-white font-semibold px-6 py-3.5 hover:brightness-110 transition text-base shadow-[0_10px_40px_-10px_rgba(248,122,42,0.55)]"
            >
                <DownloadSimple size={18} weight="bold" /> Install PawLink
            </button>
            <p className="mt-3 text-[11.5px] text-paw-bone/45">
                {deviceLabel} · {browserLabel} · takes ~5 seconds
            </p>
        </div>
    );
}

function StateSteps({ device, deviceLabel, browserLabel, steps, promptReady, onInstall }) {
    const Icon = device === "ios" ? AppleLogo : device === "android" ? GoogleLogo : DesktopTower;
    return (
        <div className="text-left" data-testid="install-steps">
            <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 rounded-2xl bg-paw-terra/15 flex items-center justify-center">
                    <Icon size={26} weight="fill" className="text-paw-terra" />
                </div>
                <div>
                    <h1 className="text-xl font-semibold text-paw-bone leading-tight">
                        Install PawLink on {deviceLabel}
                    </h1>
                    <p className="text-paw-bone/60 text-[12px] mt-0.5">
                        Using <span className="text-paw-bone/80">{browserLabel}</span> — takes 10 seconds
                    </p>
                </div>
            </div>

            {device === "ios" && (
                <div className="mb-4 flex items-center gap-2 text-[12px] text-paw-bone/70 bg-white/[0.03] border border-white/5 rounded-xl px-3 py-2">
                    <ShareNetwork size={16} weight="regular" className="text-paw-terra" />
                    Tap the Share button at the bottom of Safari to begin.
                </div>
            )}

            <ol className="space-y-3 mb-6">
                {steps.map((step, i) => (
                    <li key={i} className="flex gap-3 items-start">
                        <span className="mt-0.5 shrink-0 w-6 h-6 rounded-full bg-paw-terra text-white text-[11px] font-bold flex items-center justify-center">{i + 1}</span>
                        <span className="text-paw-bone/85 text-[14px] leading-relaxed">{step}</span>
                    </li>
                ))}
            </ol>

            <div className="flex flex-col gap-2">
                {promptReady ? (
                    <button
                        type="button"
                        onClick={onInstall}
                        data-testid="install-cta"
                        className="w-full rounded-full bg-paw-terra text-white font-semibold px-4 py-3 hover:brightness-110 transition flex items-center justify-center gap-2 shadow-[0_10px_40px_-10px_rgba(248,122,42,0.55)]"
                    >
                        <DownloadSimple size={16} weight="bold" /> Install PawLink now
                    </button>
                ) : device !== "ios" && (
                    <button
                        type="button"
                        onClick={() => window.location.reload()}
                        data-testid="install-retry"
                        className="w-full rounded-full border border-paw-terra/50 text-paw-terra font-semibold px-4 py-3 hover:bg-paw-terra/10 transition flex items-center justify-center gap-2"
                    >
                        Reload and try one-tap install
                    </button>
                )}
                <a
                    href="/"
                    data-testid="install-open-home"
                    className="w-full rounded-full border border-white/10 text-paw-bone/85 hover:text-paw-bone hover:border-white/20 font-medium px-4 py-3 transition flex items-center justify-center gap-2"
                >
                    Open PawLink in browser <ArrowSquareOut size={16} weight="regular" />
                </a>
            </div>

            <p className="mt-5 flex items-start gap-2 text-[11.5px] leading-relaxed text-paw-bone/50">
                <Info size={14} weight="regular" className="shrink-0 mt-0.5" />
                Once installed, PawLink opens like any other app — full-screen, offline-ready, and with instant scan notifications.
            </p>
        </div>
    );
}
